/**
 * Turning free text into a Deezer search query. Pure, no React, no network.
 *
 * This is the path taken whenever Claude is not configured OR the Claude call
 * fails — one code path, one fallback (DECISIONS.md). It therefore has to
 * produce something searchable from any input, including an empty string.
 */

/** What the widget searches when the user submits nothing at all. */
export const DEFAULT_QUERY = 'calm instrumental';

/** Deezer's `q` is happiest short; long sentences return nothing. */
const MAX_QUERY_CHARS = 60;

/**
 * Conversational filler that carries no musical signal. Dropping it turns
 * "i'm feeling kind of melancholy today" into "melancholy", which Deezer can
 * actually match. Deliberately small: over-stripping is worse than
 * under-stripping, because Deezer does its own fuzzy matching.
 */
const FILLER = new Set([
  'a', 'am', 'an', 'and', 'are', 'as', 'at', 'be', 'bit', 'but', 'feel',
  'feeling', 'feels', 'for', 'i', 'im', 'in', 'is', 'it', 'kind', 'kinda',
  'like', 'lot', 'me', 'my', 'of', 'really', 'so', 'some', 'something',
  'sort', 'sorta', 'the', 'to', 'today', 'very', 'want', 'wanna', 'was',
  'with',
]);

/** Deezer's advanced-search operators; a stray one silently breaks the query. */
function stripOperators(s: string): string {
  return s.replace(/["']/g, ' ').replace(/\b(?:artist|album|track|label|dur_min|dur_max|bpm_min|bpm_max):/gi, ' ');
}

/**
 * Normalise arbitrary user text into a query string. Never returns ''.
 *
 * Filler removal is skipped when it would empty the query — "I feel like it"
 * is all filler, and searching the raw text beats searching nothing.
 */
export function buildFallbackQuery(input: string): string {
  const cleaned = stripOperators(input).replace(/\s+/g, ' ').trim();
  if (cleaned === '') return DEFAULT_QUERY;

  const words = cleaned.split(' ');
  const kept = words.filter((w) => !FILLER.has(w.toLowerCase().replace(/[^a-z']/gi, '')));
  const base = kept.length > 0 ? kept.join(' ') : cleaned;

  if (base.length <= MAX_QUERY_CHARS) return base;
  // Truncate on a word boundary so we never send half a word.
  const cut = base.slice(0, MAX_QUERY_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trim();
}

/** Display helper: "mellow jazz" -> "Mellow jazz". */
export function titleCaseQuery(q: string): string {
  const first = q.charAt(0);
  return first === '' ? q : first.toUpperCase() + q.slice(1);
}
